import React, { useState } from 'react';
import './Insights.css';
import { Link } from 'react-router-dom';
import Button from '../components/Button';
import { ArrowRight } from 'lucide-react';

interface ArticleData {
  category: string;
  title: string;
  preview: string;
  date: string;
  readTime: string;
  accent: string;
}

interface InsightsProps {
  preview?: boolean;
}

const articlesList: ArticleData[] = [
  {
    category: 'AI & Automation',
    title: 'How an AI powered E-Commerce Development Company in Chennai Revolutionizes Online Business?',
    preview: 'Explore how AI-powered e-commerce development in Chennai enhances customer experiences and scales online businesses.',
    date: 'July 2, 2026',
    readTime: '6 min read',
    accent: '#3b82f6', // Blue
  },
  {
    category: 'Cloud Computing',
    title: 'Self-Driving ERP: Augmenting Humans and Automating the Rest',
    preview: 'AI-powered self-driving ERP solutions in Ambattur, Chennai that automate business processes, optimize workflows, and help organizations achieve faster, smarter, and more efficient operations.',
    date: 'June 28, 2026',
    readTime: '8 min read',
    accent: '#14b8a6', // Teal/Cyan
  },
  {
    category: 'Enterprise Software',
    title: 'AI-Powered ERP for Mid-Sized Businesses',
    preview: 'AI-powered ERP solutions help mid-sized businesses automate operations, gain real-time insights, and improve decision-making.',
    date: 'June 15, 2026',
    readTime: '5 min read',
    accent: '#6366f1', // Indigo
  },
  {
    category: 'Industrial IoT',
    title: 'Hyperautomation in ERP',
    preview: 'Empower enterprises in Tamil Nadu with AI-driven Hyperautomation in ERP that automates complex processes, enhances operational efficiency, and drives sustainable growth.',
    date: 'May 30, 2026',
    readTime: '7 min read',
    accent: '#f59e0b', // Amber
  },
];

interface InsightCardProps {
  article: ArticleData;
  idx: number;
  isExpanded: boolean;
  onToggle: () => void;
}

const InsightCard: React.FC<InsightCardProps> = ({ article, idx, isExpanded, onToggle }) => {
  const formattedIdx = String(idx + 1).padStart(2, '0');

  return (
    <div
      className={`insight-list-item ${isExpanded ? 'expanded' : ''}`}
      onClick={onToggle}
    >
      <div className="insight-list-header">
        <div className="insight-list-left">
          <span className="insight-list-index">{formattedIdx}</span>
          <h3 className="insight-list-title">{article.title}</h3>
        </div>
        
        <div className="insight-list-right">
          <span className="insight-pill">{article.category}</span>
        </div>
      </div>
      
      <div className="insight-list-body">
        <p className="insight-list-preview">{article.preview}</p>
      </div>
    </div>
  );
};

export const Insights: React.FC<InsightsProps> = ({ preview = false }) => {
  const displayList = preview ? articlesList.slice(0, 3) : articlesList;
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  return (
    <section className="section insights-section" id="insights">
      <div className="container">
        <div className="insights-split-layout">
          <div className="insights-left-col">
            <div className="section-header" style={{ textAlign: 'left', maxWidth: '100%', margin: 0 }}>
              <div className="insights-custom-tag">
                <span className="insights-dot"></span> INSIGHTS &amp; RESOURCES
              </div>
              <h2 className="insights-custom-title">
                Shaping the Future with AI, Cloud & Enterprise Innovation
              </h2>
              <p className="insights-custom-desc" style={{ marginBottom: preview ? '2rem' : '0' }}>
                Explore expert insights, technology trends, and practical guides on AI, cloud computing, enterprise software, automation, and digital transformation to help your business innovate and grow.
              </p>
              {preview && (
                <div className="insights-preview-cta">
                  <Link to="/insights">
                    <Button variant="primary" size="md" icon={<ArrowRight size={16} />}>
                      Read All Insights
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>

          <div className="insights-right-col">
            <div className="insights-list-container">
              {displayList.map((article, idx) => (
                <InsightCard 
                  key={idx} 
                  article={article} 
                  idx={idx} 
                  isExpanded={expandedIdx === idx}
                  onToggle={() => setExpandedIdx(expandedIdx === idx ? null : idx)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Insights;
